import { useState } from 'react'
import { IconUsers } from '@tabler/icons-react'
import { getBookingSchedule, type BookingResource } from '../../../lib/api'
import { useI18n } from '../../../lib/i18n'
import { SelectCheckbox } from '../ListSelection'
import { BookingTeamDialog } from './BookingTeamDialog'

export function BookingServiceTeam({ projectId, token, resources, members, busy, onAssign, onResourcesChange }: {
  projectId: string
  token: string
  resources: BookingResource[]
  members: BookingResource[]
  busy: boolean
  onAssign: (ids: string[]) => void
  onResourcesChange: (resources: BookingResource[]) => void
}) {
  const { t } = useI18n()
  const [picking, setPicking] = useState(false)
  const [managing, setManaging] = useState(false)
  const active = resources.filter((row) => row.active)
  const person = (member: BookingResource) => (
    <button type="button" className="dash-svc-team-person" onClick={() => setManaging(true)} aria-label={`${t('bookings.svc.detail.teamManage')}: ${member.name}`} aria-haspopup="dialog">
      <span className="dash-svc-team-photo" aria-hidden="true">
        {member.photoUrl ? <img src={member.photoUrl} alt="" /> : member.name.trim().split(/\s+/).slice(0, 2).map((part) => part[0] ?? '').join('').toUpperCase()}
      </span>
      <span className="dash-svc-team-copy">
        <strong>{member.name}</strong>
        {member.role ? <small>{member.role}</small> : null}
      </span>
    </button>
  )

  return (
    <div className="dash-svc-team">
      <div className="dash-svc-team-head">
        <span className="dash-svc-team-label">{t('bookings.svc.detail.team')}</span>
        {members.length ? (
          <button type="button" className="dash-svc-team-edit" onClick={() => setPicking((open) => !open)} aria-expanded={picking} disabled={busy}>
            {t(picking ? 'bookings.svc.detail.teamDone' : 'bookings.svc.detail.teamChange')}
          </button>
        ) : null}
      </div>
      {picking ? (
        <div className="dash-svc-team-picker">
          {active.map((member) => {
            const selected = members.some((row) => row.id === member.id)
            const required = selected && members.length <= 1
            return (
              <div key={member.id} className={`dash-svc-team-picker-row${selected ? ' is-on' : ''}`}>
                <SelectCheckbox
                  checked={selected}
                  label={member.name}
                  disabled={busy || required}
                  title={required ? t('bookings.svc.detail.teamRequired') : undefined}
                  onChange={() => onAssign(active.map((row) => row.id).filter((id) => id === member.id ? !selected : members.some((row) => row.id === id)))}
                />
                {person(member)}
              </div>
            )
          })}
          {active.length <= 1 ? <small className="dash-svc-team-empty">{t('bookings.svc.detail.teamEmpty')}</small> : null}
        </div>
      ) : (
        <ul>{members.map((member) => <li key={member.id}>{person(member)}</li>)}</ul>
      )}
      <button type="button" className="dash-svc-team-edit dash-svc-team-manage" aria-haspopup="dialog" onClick={() => setManaging(true)}>
        <IconUsers size={14} aria-hidden="true" /> {t('bookings.svc.detail.teamManage')}
      </button>
      {managing ? (
        <BookingTeamDialog
          projectId={projectId}
          token={token}
          resources={resources}
          onClose={() => setManaging(false)}
          onReload={async () => {
            const schedule = await getBookingSchedule(projectId, token)
            onResourcesChange(schedule.resources)
          }}
        />
      ) : null}
    </div>
  )
}
